import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AddressBox } from '../data';
import { Modal, Selectable } from '../ui';
import { useTranslation } from 'react-i18next';
import styles from './address-selector.module.scss';

const AddressSelector = ({ name = null, addresses, selectedAddressId = null, excludedAddresses = [], onChange }) => {
    const [viewAddressVisible, setViewAddresVisible] = useState(false)
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const consumedNewAddressRef = useRef(null);
    const excludedAddressesKey = excludedAddresses.join(',');
    const availableAddresses = useMemo(() => {
        const excludedAddressIds = new Set(excludedAddressesKey.split(',').filter(value => value !== '').map(Number))
        return excludedAddressIds.size ? addresses.filter(({ id }) => !excludedAddressIds.has(id)) : addresses
    }, [addresses, excludedAddressesKey])
    const preferredAddresses = useMemo(() => {
        //if there is selected address , keep it on top
        if (!selectedAddressId || addresses.findIndex(a => a.id === selectedAddressId) < 3) {
            return availableAddresses.slice(0, 3)
        }
        return [availableAddresses.find(a => a.id === selectedAddressId), ...availableAddresses.slice(0, 2)]

    }, [availableAddresses, addresses, selectedAddressId])

    useEffect(() => {
        const newAddressId = Number(searchParams.get('newAddressId'))
        const addressSelector = searchParams.get('addressSelector')
        const newAddressSignature = `${addressSelector}:${newAddressId}`

        if (!name || consumedNewAddressRef.current === newAddressSignature || addressSelector !== name || !newAddressId || !availableAddresses.some(({ id }) => id === newAddressId)) {
            return
        }

        consumedNewAddressRef.current = newAddressSignature

        if (selectedAddressId !== newAddressId) {
            onChange(newAddressId)
        }

        const nextSearchParams = new URLSearchParams(searchParams)
        nextSearchParams.delete('newAddressId')
        nextSearchParams.delete('addressSelector')
        const queryString = nextSearchParams.toString()
        window.history.replaceState(null, '', `${window.location.pathname}${queryString ? `?${queryString}` : ''}${window.location.hash}`)
    }, [availableAddresses, name, onChange, searchParams, selectedAddressId])

    return (
        <div className={styles.addressSelector}>
            <Selectable
                items={preferredAddresses}
                onSelectionChange={(id) => { onChange(id) }}
                renderItem={(address, isSelected) => <AddressBox key={address.id} id={address.id} address={address} isSelected={isSelected} className={styles.addressCard} />}
                selected={selectedAddressId}
                className={styles.addresses}
            />
            <div className="actions">
                {availableAddresses.length > 3 && (
                    <>
                        <Modal title={t("Select addresses")} isVisible={viewAddressVisible} onClose={() => setViewAddresVisible(false)} width='min(1040px, 95vw)'>
                            <Selectable
                                items={availableAddresses}
                                onSelectionChange={(id) => {
                                    onChange(id)
                                    setViewAddresVisible(false)
                                }}
                                renderItem={(address, isSelected) => <AddressBox key={address.id} id={address.id} address={address} isSelected={isSelected} className={styles.addressCard} />}
                                selected={selectedAddressId}
                                className={styles.addresses}
                            />
                        </Modal>
                        <button onClick={() => setViewAddresVisible(true)} className="button--primary">{t("View More")}</button>
                    </>
                )}
                <Link to={{
                    pathname: './add',
                    search: name ? `?addressSelector=${encodeURIComponent(name)}` : ''
                }} className="button--primary">{t('Add New Address')}</Link>
            </div>
            {!!name && <input type="hidden" name={name} value={selectedAddressId} />}
        </div>
    )
}
AddressSelector.propTypes = {
    name: PropTypes.string,
    addresses: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired
    })),
    selectedAddressId: PropTypes.number,
    excludedAddresses: PropTypes.arrayOf(PropTypes.number),
    onChange: PropTypes.func.isRequired,
}

export default AddressSelector
